/** @typedef {import('../types/auth').MerchantUser} MerchantUser */
/** @typedef {import('../types/auth').UserContextValue} UserContextValue */

import PropTypes from 'prop-types';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { decodeJwt } from '../utils/token-resolver';

const APP_NAME = 'basqet';

/** @type {React.Context<UserContextValue>} */
const UserContext = createContext();

const readStoredUser = () => {
  const stored = sessionStorage.getItem(APP_NAME);
  if (!stored) return null;
  try {
    const { merchantUser, admin } = JSON.parse(stored);
    return merchantUser || admin || null;
  } catch {
    return null;
  }
};

// Identity fields come from the OIDC profile/id_token. Authorization
// fields (notably `permissions`) come from the polled `auth/refresh`
// query (see useFetchMerchantProfile) via setUser — NOT from the profile.
// We therefore MERGE the profile over whatever the user object already
// holds (prior state, or the stored merchantUser on a fresh load) instead
// of replacing it. Replacing would drop `permissions` every time OIDC
// silently renews the token, which flips route guards into a spurious
// "Page not found" until the next refresh poll repopulates them.
const mergeOidcProfile = (oidcUser, base) => {
  const profile = oidcUser?.profile || {};
  const decoded = decodeJwt(oidcUser?.id_token);
  return {
    ...(base || {}),
    ...profile,
    given_name: decoded?.given_name || profile?.given_name
  };
};

const UserProvider = ({ children, oidcUser }) => {
  const [user, setUser] = useState(() => {
    if (oidcUser) {
      return mergeOidcProfile(oidcUser, readStoredUser());
    }

    return readStoredUser() || {};
  });

  useEffect(() => {
    if (oidcUser) {
      setUser((prev) => mergeOidcProfile(oidcUser, prev));
      return;
    }

    const stored = readStoredUser();
    if (stored) {
      setUser(stored);
    }
  }, [oidcUser]);

  const userHasVerifiedEmail = Boolean(user?.email_verified || user?.emailVerified);
  return (
    <UserContext.Provider value={{ user, setUser, userHasVerifiedEmail }}>
      {children}
    </UserContext.Provider>
  );
};

/** @returns {UserContextValue} */
const useUserContext = () => useContext(UserContext);

UserProvider.propTypes = {
  children: PropTypes.node,
  oidcUser: PropTypes.object
};
export { UserContext, UserProvider, useUserContext };
